import React, { useState, useEffect } from 'react';
import { referenceDocumentsApi, projectsApi } from '../../services/api';
import { logger } from '../../utils/logger';
import ConfirmationModal from '../ConfirmationModal';
import ReferenceUploadModal from './ReferenceUploadModal';
import { useToast, Button, Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, Label, Input } from '@/components/ui';
import type { ReferenceScopeType } from '../../types/panel-types';

interface ReferenceDocument {
  documentId: string;
  title: string;
  documentType: ReferenceScopeType;
  standardNumber?: string;
  description?: string;
  indexStatus: string;
  chunkCount?: number;
  tags: string[];
  // Enhanced metadata fields
  fileFormat?: string;         // PDF, DOCX, XLSX, TXT
  standardType?: string;       // DO-178C, ARP4761A, etc.
  version?: string;
  effectiveDate?: string;
  createdAt?: string;
  uploadedBy?: string;
  // Project assignment - empty = All Projects
  assignedProjects?: { projectId: string; projectName: string; projectCode?: string }[];
}

interface ProjectReferencesPanelProps {
  projectId: string;
  projectName?: string;
}

export default function ProjectReferencesPanel({ projectId, projectName }: ProjectReferencesPanelProps) {
  const [projectDocuments, setProjectDocuments] = useState<ReferenceDocument[]>([]);
  const [libraryStandards, setLibraryStandards] = useState<ReferenceDocument[]>([]);
  const [allReferences, setAllReferences] = useState<ReferenceDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showUnlinkModal, setShowUnlinkModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [documentToUnlink, setDocumentToUnlink] = useState<ReferenceDocument | null>(null);
  const [libraryExpanded, setLibraryExpanded] = useState(false);  // Collapsed by default
  
  // Promote to Reference Library modal
  const [showPromoteModal, setShowPromoteModal] = useState(false);
  const [documentToPromote, setDocumentToPromote] = useState<ReferenceDocument | null>(null);
  const [isPromoting, setIsPromoting] = useState(false);
  
  // Share to Projects modal
  const [showShareModal, setShowShareModal] = useState(false);
  const [documentToShare, setDocumentToShare] = useState<ReferenceDocument | null>(null);
  const [selectedShareProjects, setSelectedShareProjects] = useState<string[]>([]);
  const [availableShareProjects, setAvailableShareProjects] = useState<{ id: string; name: string; projectCode?: string }[]>([]);
  const [isSharing, setIsSharing] = useState(false);
  const [loadingShareProjects, setLoadingShareProjects] = useState(false);
  
  // Delete document modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<ReferenceDocument | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  const { error, success } = useToast();

  useEffect(() => {
    loadReferences();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadReferences is defined below, stable function
  }, [projectId]);

  const loadReferences = async () => {
    try {
      setLoading(true);
      
      // Load references available to this project (All Projects + specifically assigned)
      const linked = await referenceDocumentsApi.getProjectReferences(projectId);
      const allLinked = linked.references || [];
      
      // Separate documents:
      // - Project documents: specifically assigned to this project
      // - All Projects documents: available to all (no assignedProjects or empty array)
      const projectDocs = allLinked.filter(doc => {
        const assigned = doc.assignedProjects || [];
        // assignedProjects can be array of strings OR array of objects with projectId
        return assigned.length > 0 && assigned.some((p: string | { projectId: string }) => 
          typeof p === 'string' ? p === projectId : p.projectId === projectId
        );
      });
      const allProjectsDocs = allLinked.filter(doc => {
        const assigned = doc.assignedProjects || [];
        return assigned.length === 0;
      });
      
      setProjectDocuments(projectDocs);
      setLibraryStandards(allProjectsDocs);
      
      // Load all references for linking/assigning
      const all = await referenceDocumentsApi.list({ indexStatus: 'completed' });
      setAllReferences(all);
    } catch (err) {
      logger.error('Error loading references:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLink = async (documentId: string) => {
    try {
      await referenceDocumentsApi.linkToProject(projectId, documentId);
      await loadReferences();
      setShowLinkModal(false);
    } catch (err: unknown) {
      error('Link Failed', 'Error linking reference: ' + (err instanceof Error ? err.message : 'An unexpected error occurred'));
    }
  };

  // Kept for potential future use - currently "All Projects" docs can't be unlinked
  const _handleUnlink = (documentId: string) => {
    const doc = libraryStandards.find(d => d.documentId === documentId);
    if (doc) {
      setDocumentToUnlink(doc);
      setShowUnlinkModal(true);
    }
  };
  void _handleUnlink; // suppress unused warning

  const confirmUnlink = async () => {
    if (!documentToUnlink) return;

    await referenceDocumentsApi.unlinkFromProject(projectId, documentToUnlink.documentId);
    setShowUnlinkModal(false);
    setDocumentToUnlink(null);
    await loadReferences();
  };

  // Promote document to Reference Library (All Projects scope)
  const handlePromote = async () => {
    if (!documentToPromote) return;
    
    setIsPromoting(true);
    try {
      await referenceDocumentsApi.promote(documentToPromote.documentId);
      success('Document Promoted', `${documentToPromote.standardNumber || documentToPromote.title} is now available to all projects.`);
      setShowPromoteModal(false);
      setDocumentToPromote(null);
      await loadReferences();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      error('Promote Failed', errorMessage);
    } finally {
      setIsPromoting(false);
    }
  };

  // Open share modal and load available projects
  const openShareModal = async (doc: ReferenceDocument) => {
    setDocumentToShare(doc);
    setSelectedShareProjects([]);
    setShowShareModal(true);
    
    // Load projects for selection
    setLoadingShareProjects(true);
    try {
      const projects = await projectsApi.list();
      // Filter out projects this doc is already assigned to
      const currentlyAssigned = (doc.assignedProjects || []).map(p => p.projectId);
      const available = projects
        .filter((p: { id: string }) => !currentlyAssigned.includes(p.id) && p.id !== projectId)
        .map((p: { id: string; name: string; projectCode?: string }) => ({
          id: p.id,
          name: p.name,
          projectCode: p.projectCode
        }));
      setAvailableShareProjects(available);
    } catch (err) {
      logger.error('Failed to load projects for sharing:', err);
    } finally {
      setLoadingShareProjects(false);
    }
  };

  // Share document to selected projects
  const handleShare = async () => {
    if (!documentToShare || selectedShareProjects.length === 0) return;
    
    setIsSharing(true);
    try {
      await referenceDocumentsApi.share(documentToShare.documentId, selectedShareProjects);
      success('Document Shared', `${documentToShare.standardNumber || documentToShare.title} is now shared with ${selectedShareProjects.length} project(s).`);
      setShowShareModal(false);
      setDocumentToShare(null);
      setSelectedShareProjects([]);
      await loadReferences();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      error('Share Failed', errorMessage);
    } finally {
      setIsSharing(false);
    }
  };

  // Delete document permanently
  const handleDelete = async () => {
    if (!documentToDelete) return;
    
    setIsDeleting(true);
    try {
      await referenceDocumentsApi.delete(documentToDelete.documentId);
      success('Document Deleted', `${documentToDelete.standardNumber || documentToDelete.title} has been permanently deleted.`);
      setShowDeleteModal(false);
      setDocumentToDelete(null);
      await loadReferences();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      error('Delete Failed', errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  // References available to assign to this project (not already assigned)
  const availableToLink = allReferences.filter(ref => {
    // Exclude docs already assigned to this project
    const alreadyAssigned = [...projectDocuments].some(
      linked => linked.documentId === ref.documentId
    );
    // Exclude "All Projects" docs (they're already available)
    const isAllProjects = !ref.assignedProjects || ref.assignedProjects.length === 0;
    return !alreadyAssigned && !isAllProjects;
  });

  if (loading) {
    return <div className="text-center py-4 text-muted-foreground">Loading references...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Section 1: Project Documents */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-foreground">Project Documents</h3>
          <Button
            variant="success"
            size="sm"
            onClick={() => setShowUploadModal(true)}
            className="flex items-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            Upload
          </Button>
        </div>

        {projectDocuments.length === 0 ? (
          <div className="bg-muted border-2 border-dashed border-border rounded-lg p-6 text-center">
            <p className="text-muted-foreground">No project-specific documents yet.</p>
            <p className="text-sm text-muted-foreground mt-1">
              Upload OEM specifications, customer requirements, or certification basis documents.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {projectDocuments.map((doc) => (
              <div
                key={doc.documentId}
                className="bg-card border border-border rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 cursor-pointer">
                    {/* Document Number (prominent) */}
                    <h4 className="font-semibold text-foreground text-lg">
                      {doc.standardNumber || doc.title}
                    </h4>
                    
                    {/* Title (if different from document number) */}
                    {doc.title && doc.standardNumber && doc.title !== doc.standardNumber && (
                      <p className="text-sm text-foreground mt-0.5">{doc.title}</p>
                    )}
                    
                    {/* Metadata row: Revision, Date */}
                    <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                      {doc.version && (
                        <span>Rev: {doc.version}</span>
                      )}
                      {doc.createdAt && (
                        <span>{new Date(doc.createdAt).toLocaleDateString()}</span>
                      )}
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="xs"
                      onClick={() => openShareModal(doc)}
                      className="text-xs text-muted-foreground hover:text-foreground"
                      title="Share with other projects"
                    >
                      Share
                    </Button>
                    <Button
                      variant="ghost"
                      size="xs"
                      onClick={() => {
                        setDocumentToPromote(doc);
                        setShowPromoteModal(true);
                      }}
                      className="text-xs text-muted-foreground hover:text-foreground"
                      title="Move to Reference Library (All Projects)"
                    >
                      Move to Library
                    </Button>
                    <Button
                      variant="ghost"
                      size="xs"
                      onClick={() => {
                        setDocumentToDelete(doc);
                        setShowDeleteModal(true);
                      }}
                      className="text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
                      title="Delete document permanently"
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Section 2: Library Standards (collapsed by default) */}
      <div className="space-y-2">
        {/* Collapsible header */}
        <Button
          variant="ghost"
          onClick={() => setLibraryExpanded(!libraryExpanded)}
          className="w-full flex items-center justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted h-auto"
        >
          <div className="flex items-center gap-2">
            <span className="text-lg">{libraryExpanded ? '▼' : '▶'}</span>
            <span className="font-medium text-foreground">
              Library Standards ({libraryStandards.length} available)
            </span>
          </div>
          <span className="text-sm text-muted-foreground">
            View Library →
          </span>
        </Button>
        
        {/* Expanded content */}
        {libraryExpanded && (
          <div className="pl-4 space-y-2">
            {libraryStandards.length === 0 ? (
              <p className="text-sm text-muted-foreground py-2">
                No customer-wide standards uploaded yet. Upload from the Reference Library.
              </p>
            ) : (
              libraryStandards.map((doc) => (
                <div
                  key={doc.documentId}
                  className="flex items-center justify-between p-3 bg-card border border-border rounded-lg hover:bg-muted/30 cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <span className="font-medium text-foreground">
                      {doc.standardNumber || doc.title}
                    </span>
                    {doc.version && (
                      <span className="text-xs text-muted-foreground">Rev: {doc.version}</span>
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {doc.documentType === 'industry-standard' ? 'Standard' : 'Reference'}
                  </span>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Link Reference Modal */}
      <Dialog open={showLinkModal} onOpenChange={setShowLinkModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Link Reference to Project</DialogTitle>
          </DialogHeader>

          <div className="overflow-y-auto flex-1 py-4">
            {availableToLink.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                All available references are already linked to this project.
              </div>
            ) : (
              <div className="space-y-2">
                {availableToLink.map((doc) => (
                  <div
                    key={doc.documentId}
                    className="border border-border rounded-lg p-4 hover:bg-muted transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        {/* Title row with file format badge */}
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                            doc.fileFormat === 'pdf' ? 'bg-destructive-light text-destructive' :
                            doc.fileFormat === 'docx' ? 'bg-info-light text-info' :
                            doc.fileFormat === 'xlsx' ? 'bg-success-light text-success' :
                            doc.fileFormat === 'txt' ? 'bg-secondary text-muted-foreground' :
                            'bg-secondary text-muted-foreground'
                          }`}>
                            {(doc.fileFormat || 'PDF').toUpperCase()}
                          </span>
                          <h4 className="font-medium text-foreground">{doc.title}</h4>
                          {doc.standardNumber && (
                            <span className="text-xs bg-secondary text-muted-foreground px-2 py-0.5 rounded">
                              {doc.standardNumber}
                            </span>
                          )}
                        </div>
                        {doc.description && (
                          <p className="text-sm text-muted-foreground mt-1">{doc.description}</p>
                        )}
                        {/* Metadata row */}
                        <div className="flex gap-2 mt-2 flex-wrap">
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            doc.documentType === 'industry-standard'
                              ? 'bg-secondary text-muted-foreground'
                              : 'bg-info-light text-info'
                          }`}>
                            {doc.documentType === 'industry-standard' ? 'Standard' : 'Company'}
                          </span>
                          {doc.scope && (
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              doc.scope === 'global' ? 'bg-warning-light text-warning' :
                              doc.scope === 'company' ? 'bg-info-light text-info' :
                              'bg-secondary text-muted-foreground'
                            }`}>
                              {doc.scope === 'global' ? 'Global' : doc.scope === 'company' ? 'Company' : 'Project'}
                            </span>
                          )}
                          {doc.standardType && (
                            <span className="text-xs bg-muted text-foreground px-2 py-0.5 rounded">
                              {doc.standardType}
                            </span>
                          )}
                          {(doc.tags || []).map(tag => (
                            <span key={tag} className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => handleLink(doc.documentId)}
                        className="ml-4"
                      >
                        Link
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="secondary" onClick={() => setShowLinkModal(false)} className="w-full">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unlink Confirmation Modal */}
      {showUnlinkModal && documentToUnlink && (
        <ConfirmationModal
          title="Unlink Reference"
          message="Remove this reference from your project? It will still be available in the Reference Library."
          confirmText="Unlink"
          cancelText="Cancel"
          isDanger={false}
          onConfirm={confirmUnlink}
          onCancel={() => {
            setShowUnlinkModal(false);
            setDocumentToUnlink(null);
          }}
        >
          <div className="p-3 bg-muted border border-border rounded-lg">
            <div className="mb-2">
              <strong>Title:</strong> {documentToUnlink.title}
            </div>
            {documentToUnlink.standardNumber && (
              <div className="mb-2">
                <strong>Standard Number:</strong> {documentToUnlink.standardNumber}
              </div>
            )}
            <div>
              <strong>Type:</strong> {documentToUnlink.documentType === 'industry-standard' ? 'Industry Standard' : 'Company Reference'}
            </div>
          </div>
        </ConfirmationModal>
      )}

      {/* Upload Modal - auto-links to this project */}
      {showUploadModal && (
        <ReferenceUploadModal
          projectId={projectId}
          projectName={projectName}
          onClose={() => setShowUploadModal(false)}
          onSuccess={() => {
            setShowUploadModal(false);
            loadReferences();
          }}
        />
      )}

      {/* Promote to Reference Library Modal */}
      <Dialog open={showPromoteModal} onOpenChange={setShowPromoteModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Move to Reference Library?</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-sm text-muted-foreground">
              This document will become available to <strong>all projects</strong> customer-wide.
            </p>
            {documentToPromote && (
              <div className="p-3 bg-muted/30 rounded-lg">
                <p className="font-medium">{documentToPromote.standardNumber || documentToPromote.title}</p>
                {documentToPromote.title && documentToPromote.standardNumber && (
                  <p className="text-sm text-muted-foreground">{documentToPromote.title}</p>
                )}
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Administrators can reverse this action later if needed.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowPromoteModal(false);
                setDocumentToPromote(null);
              }}
              disabled={isPromoting}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handlePromote}
              disabled={isPromoting}
            >
              {isPromoting ? 'Moving...' : 'Move to Library'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share to Projects Modal */}
      <Dialog open={showShareModal} onOpenChange={(open) => {
        if (!open) {
          setShowShareModal(false);
          setDocumentToShare(null);
          setSelectedShareProjects([]);
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Share to Projects</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {documentToShare && (
              <div className="p-3 bg-muted/30 rounded-lg">
                <p className="font-medium">{documentToShare.standardNumber || documentToShare.title}</p>
                {documentToShare.title && documentToShare.standardNumber && (
                  <p className="text-sm text-muted-foreground">{documentToShare.title}</p>
                )}
              </div>
            )}
            
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Select projects to share this document with:
              </p>
              
              {loadingShareProjects ? (
                <div className="text-center py-4 text-muted-foreground">
                  Loading projects...
                </div>
              ) : availableShareProjects.length === 0 ? (
                <div className="text-center py-4 text-muted-foreground">
                  No other projects available to share with.
                </div>
              ) : (
                <div className="max-h-48 overflow-y-auto space-y-2 border border-border rounded-lg p-2">
                  {availableShareProjects.map(proj => (
                    <Label
                      key={proj.id}
                      className="flex items-center gap-2 p-2 hover:bg-muted/50 rounded cursor-pointer font-normal"
                    >
                      <Input
                        type="checkbox"
                        checked={selectedShareProjects.includes(proj.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedShareProjects([...selectedShareProjects, proj.id]);
                          } else {
                            setSelectedShareProjects(selectedShareProjects.filter(id => id !== proj.id));
                          }
                        }}
                        className="rounded border-input w-4 h-4"
                      />
                      <span className="text-sm">
                        {proj.projectCode || proj.name}
                        {proj.projectCode && proj.name && proj.projectCode !== proj.name && (
                          <span className="text-muted-foreground ml-1">({proj.name})</span>
                        )}
                      </span>
                    </Label>
                  ))}
                </div>
              )}
            </div>
            
            <p className="text-xs text-muted-foreground">
              This project will remain the owner. Other projects can view but not edit.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowShareModal(false);
                setDocumentToShare(null);
                setSelectedShareProjects([]);
              }}
              disabled={isSharing}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleShare}
              disabled={isSharing || selectedShareProjects.length === 0}
            >
              {isSharing ? 'Sharing...' : `Share to ${selectedShareProjects.length} Project(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={(open) => {
        if (!open && !isDeleting) {
          setShowDeleteModal(false);
          setDocumentToDelete(null);
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-destructive">Delete Reference Document</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-sm text-muted-foreground">
              Are you sure you want to permanently delete this document? This action <strong>cannot be undone</strong>.
            </p>
            {documentToDelete && (
              <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                <p className="font-medium text-foreground">{documentToDelete.standardNumber || documentToDelete.title}</p>
                {documentToDelete.title && documentToDelete.standardNumber && documentToDelete.title !== documentToDelete.standardNumber && (
                  <p className="text-sm text-muted-foreground">{documentToDelete.title}</p>
                )}
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              This will remove the document, all indexed content, and search data.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteModal(false);
                setDocumentToDelete(null);
              }}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete Permanently'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
